import { useEffect, useLayoutEffect, useRef } from 'react';

// Auto-resizing textarea — starts as a single line, grows to fit content.
// Module scope keeps the component identity stable across parent renders
// (a nested definition would unmount/remount and lose focus on every keystroke).
export function AutoResizeTextarea({
  value,
  onChange,
  className,
  placeholder,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      aria-label={ariaLabel}
      rows={1}
      className={`resize-none overflow-hidden leading-[1.4] ${className ?? ''}`}
    />
  );
}

// Textarea for editing raw JSON: Tab inserts two spaces instead of moving
// focus, and the caret is restored after the resulting state update. Keyboard
// users leave the field with Shift+Tab, or Escape followed by Tab.
export function JsonTextarea({
  id,
  value,
  onChange,
  className,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const releaseTabRef = useRef(false);

  useEffect(() => {
    if (pendingCursorRef.current !== null && ref.current) {
      ref.current.selectionStart = pendingCursorRef.current;
      ref.current.selectionEnd = pendingCursorRef.current;
      pendingCursorRef.current = null;
    }
  }, [value]);

  return (
    <textarea
      ref={ref}
      id={id}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          releaseTabRef.current = true;
          return;
        }
        const released = releaseTabRef.current;
        releaseTabRef.current = false;
        if (e.key !== 'Tab' || e.shiftKey || released) return;
        e.preventDefault();
        const ta = e.currentTarget;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        pendingCursorRef.current = start + 2;
        onChange(value.slice(0, start) + '  ' + value.slice(end));
      }}
      onBlur={() => { releaseTabRef.current = false; }}
      className={className}
    />
  );
}
